<template>
  <div :class="{ 'mt-3': id === 0, 'mt-5': id > 0 }">
    <h1>{{ $t('Dog') }} #{{ id+1 }} </h1>
    <v-layout row wrap>

      <v-flex xs12 sm6 class="pr-5">
        <v-text-field
          :label="$t('Name of the dog on the pedigree')"
          v-model="fields.name"
          :rules="nameRules"
          required
        />
      </v-flex>
      <v-flex xs12 sm6>
        <v-select
          :items="genders"
          item-value="key"
          item-text="value"
          v-model="fields.gender"
          :label="$t('Gender')"
          :rules="nameRules"
          single-line
        ></v-select>
      </v-flex>

      <v-flex xs12 sm6 class="pr-5">
        <v-menu
          ref="menu"
          lazy
          :close-on-content-click="false"
          v-model="menu"
          transition="scale-transition"
          offset-y
          full-width
          min-width="290px"
        >
          <v-text-field
            slot="activator"
            :label="$t('Date of birth')"
            :rules="nameRules"
            v-model="fields.birthday"
            required
            readonly
          />
          <v-date-picker
            ref="picker"
            v-model="fields.birthday"
            @change="save"
            min="1995-01-01"
            :max="new Date().toISOString().substr(0, 10)"
          />
        </v-menu>
      </v-flex>
      <v-flex xs12 sm6>
        <v-select
          :items="colors"
          item-value="key"
          item-text="value"
          v-model="fields.colour"
          :label="$t('Colour')"
          :rules="nameRules"
          single-line
        ></v-select>
      </v-flex>
      <v-flex xs12 sm6 class="pr-5">
        <v-text-field
          :label="$t('Chip number')"
          v-model="fields.chipnumber"
          :rules="nameRules"
          required
        />
      </v-flex>
      <v-flex xs12 sm6>
        <v-text-field
          :label="$t('Pedigree number')"
          v-model="fields.pedigreenumber"
          :rules="nameRules"
          hint="NHSB / LOSH / VDH-CFH / VDH-BCD / LOF / etc"
          required
        />
      </v-flex>
      <v-flex xs12 md12>
        <v-text-field
          :label="$t('Breeder')"
          v-model="fields.breeder"
          :rules="nameRules"
          required
        />
      </v-flex>
      <v-flex xs12 sm6 class="pr-5">
        <v-text-field
          :label="$t('Sire')"
          v-model="fields.sire"
          :rules="nameRules"
          required
        />
      </v-flex>
      <v-flex xs12 sm6>
        <v-text-field
          :label="$t('Dame')"
          v-model="fields.dame"
          :rules="nameRules"
          required
        ></v-text-field>
      </v-flex>
      <v-flex xs12>
        <h3>{{ $t('Co-owner') }} ({{ $t('Optional') }})</h3>
      </v-flex>
      <v-flex xs12 sm6 class="pr-5">
        <v-text-field
          :label="$t('First name')"
          v-model="fields.coowner_firstname"
        ></v-text-field>
      </v-flex>
      <v-flex xs12 sm6>
        <v-text-field
          :label="$t('Last Name')"
          v-model="fields.coowner_lastname"
        ></v-text-field>
      </v-flex>
    </v-layout>
    <event-component
      v-for="event in events"
      v-bind="event"
      :birthday="fields.birthday"
      :key="event.name"
      :value="fields.events[event.keyword]"
      :discount-multiple-dogs="fields.discountMultipleDogs"
      :discount-multiple-dogs-enabled="event.discountMultipleDogs"
      :show-breeder-group="event.showBreederGroup"
      @update="(val) => { updateEvent(event.keyword, val) }"
    >
    </event-component>


    <div class="ml-2" v-if="fields.hasBehaviourTest">
      <label>{{ $t('Do behaviourtest on')}}:</label>
      <v-radio-group v-model="value.behaviourDay" :rules="nameRules" row>
        <v-radio
          :label="$t('Saturday')"
          value="saturday"
        ></v-radio>
        <v-radio
          :label="$t('Sunday')"
          value="sunday"
        ></v-radio>
      </v-radio-group>
    </div>

    <template v-if="$config.isBriardWeekend">
      <v-checkbox :label="discountMultipleEventsLabel" :readonly="true"
                  :input-value="value.discountMultipleEventsPerDog">
      </v-checkbox>
    </template>

      <template>
      <v-checkbox
        v-if="events.some(e => e.showBreederGroup)"
        :label="$t('Participate in breeder group (3 - 5 dogs)')"
        v-model="fields.breeder_group"></v-checkbox>
    </template>

    <div v-if="requiredDocuments.length > 0 || fields.files.length > 0">
    <h2>{{ $t('Documents' )}}</h2>
    <DragDropWatcher v-on:drop="handleDrop">
      <form enctype="multipart/form-data">

        <input hidden
               accept="image/jpeg, image/png, application/pdf, .doc, .docx"
               type='file'
               :multiple='true'
               data-tap-disabled="true"
               @change='processFile($event)'
               ref="fileInput"
               class='input-file'
        />
        <div style="text-align: center">{{ $t('Drag files here or') }}
        <v-btn color="info" small @click="uploadFileClick">{{ $t('Upload files') }}</v-btn>
        </div>
        <ul class="uploadList">

          <li><strong>{{ $t('Required documents') }}:</strong></li>
          <li v-for="document in requiredDocuments" v-bind:key="document">
            <v-icon>check_box_outline_blank</v-icon> {{ document }}
          </li>

          <br />
          <li><em>{{ $t('Accepted file types: doc, docx, pdf, jpg, jpeg, png, gif') }}</em></li>
        </ul>

        <br />
        <ul class="uploadList">
          <li v-if="fields.files.length > 0">
            <strong>{{ $t('Uploaded files:') }}</strong>
          </li>
          <li v-for="(file, index) in fields.files" v-bind:key="`file-${index}`">
            <v-icon>check</v-icon> {{ file }}
          </li>
        </ul>
        <div ref="fileQueue">
          <div v-for="(file, index) in fileQueue" v-bind:key="`queue-${index}`">
            <template v-if="file.status !== 'DONE'">
              <div  v-if="file.status === 'TODO'">
                {{ file.data.name }}<div style="text-align: right">{{ file.progress }}%</div>
              </div>
              <v-progress-linear  v-if="file.status === 'TODO'"
                                  :value="file.progress" color="success">
              </v-progress-linear>
              <template v-else-if="file.status === 'ERROR'">
                <v-alert v-model="file.showError" type="error" dismissible>
                  Could not upload {{ file.data.name }}: {{ file.error }}
                </v-alert>
              </template>

            </template>
          </div>
        </div>
      </form>
    </DragDropWatcher>
    </div>
    <div class="text-xs-right" v-if="isLast && id !== 0">
      <v-btn @click="$emit('delete', id)" small color="error">
        {{ $t('Delete dog')}} #{{ id + 1}}
      </v-btn>
    </div>
  </div>
</template>

<script>
// import Vue from 'vue';
import moment from 'moment';
import 'moment/min/locales';
import axios from 'axios';
import EventComponent from './EventComponent';
import DragDropWatcher from './DragDropWatcher';
import config from '../config';

export default {
  components: { EventComponent, DragDropWatcher },
  name: 'dog-component',
  props: {
    id: {
      type: Number,
    },
    isLast: {
      type: Boolean,
    },
    events: {
      type: Array,
    },
    value: {
      type: Object,
    },
  },
  data() {
    const data = {
      fields: {
        birthday: '',
        name: '',
        colour: null,
        daydate: this.$config.date,
        valid: false,
        chipnumber: '',
        pedigreenumber: '',
        breeder: '',
        gender: '',
        sire: '',
        dame: '',
        coowner_firstname: '',
        coowner_lastname: '',
        events: {},
        files: {},
        discountMultipleEventsPerDog: false,
        discountMultipleDogs: false,
        behaviourDay: '',
        hasBehaviourTest: false,
        breeder_group: false,
      },
      requiredDocuments: [],
      menu: false,
      nameRules: [
        v => !!v || this.$t('Required'),
      ],
      fileQueue: [],
    };
    Object.keys(this.value).forEach((key) => {
      data.fields[key] = this.value[key];
    });
    Object.values(this.events).forEach((event) => {
      if (!(event.keyword in data.fields.events)) {
        data.fields.events[event.keyword] = '';
      }
    });
    return data;
  },
  watch: {
    menu(val) {
      return val && this.$nextTick(() => {
        this.$refs.picker.activePicker = 'YEAR';
      });
    },
    // eslint-disable-next-line no-unused-vars,func-names
    'fields.birthday': function (value, oldvalue) {
      Object.values(this.events).forEach((event) => {
        this.$set(this.fields.events, event.keyword, '');
      });
    },
    fields: {
      deep: true,
      handler(value) {
        this.$emit('update', value);
      },
    },
    value: {
      deep: true,
      handler() {
        this.updatedRequiredDocuments();
      },
    },
  },
  mounted() {
    if (this.value && this.value.events) {
      Object.keys(this.value.events).forEach((key) => {
        this.fields.events[key] = this.value.events[key];
      });
      this.updatedRequiredDocuments();
    }
  },
  computed: {
    discountMultipleEventsLabel() {
      // eslint-disable-next-line max-len
      return `${this.$t('Discount for participating in both events: (Except baby and puppy class)')}: -${this.formatCurrency(this.$config.discount.multipleEventsPerDog)}`;
    },

    colors() {
      return [
        { key: 'zwart', value: this.$t('Black') },
        { key: 'grijs', value: this.$t('Grey') },
        { key: 'fauve', value: this.$t('Fawn') },
      ];
    },
    genders() {
      return [
        { key: 'reu', value: this.$t('Dog-gender') },
        { key: 'teef', value: this.$t('Bitch') },
      ];
    },
    difference() {
      return moment(this.daydate).diff(this.date, 'months', true);
    },
  },
  methods: {
    formatCurrency(num) {
      // Create our number formatter.
      const formatter = new Intl.NumberFormat('nl-NL', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        // the default value for minimumFractionDigits depends on the currency
        // and is usually already 2
      });
      return formatter.format(num / 100);
    },
    updatedRequiredDocuments() {
      const output = [];
      if (!this.value || !this.value.events) {
        this.requiredDocuments = output;
      }

      const selectionChoice = this.fields.events.selection;
      const eventWithChoices = this.events.find(event => event.choices && event.choices.some(choice => choice.keyword === selectionChoice));
      const definition = eventWithChoices ? eventWithChoices.choices.find(e => e.keyword === selectionChoice) : null;
      const hasTest = definition != null && definition.has_behaviour_test;
      this.$set(this.fields, 'hasBehaviourTest', hasTest);

      let hasMultipleDogDiscount = 0;

      Object.keys(this.value.events).forEach((eventKey) => {
        const eventValue = this.value.events[eventKey];
        const event = this.events.find(e => e.keyword === eventKey);
        if (!event) return;
        const eventChoice = event.choices.find(e => e.keyword === eventValue);
        // nothing is chosen..
        if (!eventChoice) return;
        if (eventChoice.discountMultipleEventsPerDog) {
          hasMultipleDogDiscount += 1;
        }
        if (!eventChoice.documents) return;
        eventChoice.documents.forEach(d => output.push(d));
      });
      this.$set(this.value, 'discountMultipleEventsPerDog', hasMultipleDogDiscount === Object.keys(this.value.events).length);
      this.requiredDocuments = output;
    },
    save(date) {
      this.$refs.menu.save(date);
    },
    updateEvent(type, value) {
      this.$set(this.fields.events, type, value);
    },
    handleDrop(event) {
      this.visibleStatus = true;
      for (let i = 0; i < event.dataTransfer.files.length; i += 1) {
        this.fileQueue.push({
          data: event.dataTransfer.files[i],
          progress: 0,
          status: 'TODO',
          error: '',
          showError: false,

        });
      }
      this.uploadNextFile();
    },
    processFile(event) {
      for (let i = 0; i < event.target.files.length; i += 1) {
        this.fileQueue.push({
          data: event.target.files[i],
          progress: 0,
          status: 'TODO',
          error: '',
          showError: false,
        });
      }
      this.uploadNextFile();
      // eslint-disable-next-line no-param-reassign
      event.target.value = '';
    },
    uploadNextFile() {
      // find next file.
      const file = this.fileQueue.find(element => element.status === 'TODO');
      if (file === null || typeof file === 'undefined' || this.isUploading) {
        return;
      }
      this.isUploading = true;

      // const vm = this;
      const axiosConfig = {
        onUploadProgress(progressEvent) {
          file.progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        },
        withCredentials: true,
      };
      const url = `${config.urlPrefix}/upload`;
      const data = new FormData();
      // const self = this;
      data.append('upload[filename]', file.data);
      axios.post(url, data, axiosConfig).then((response) => {
        if (response.data.status === 'OK') {
          file.status = 'DONE';
          // if something happens..
          this.$set(this.fields.files, response.data.hash, response.data.name);
        } else {
          file.error = response.data.error;
          file.status = 'ERROR';
          file.showError = true;
        }
        this.isUploading = false;
        this.uploadNextFile();
      }).catch((err) => {
        file.status = 'ERROR';
        file.error = err;
        file.showError = true;
        this.isUploading = false;
      });
    },
    uploadFileClick() {
      this.$refs.fileInput.click();
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style>
  h1, h2 {
    font-weight: normal;
  }

  ul {
    list-style-type: none;
    padding: 0;
  }

  ul.uploadList li {
    display: block;
    list-style-type: square;
  }

  li {
    display: inline-block;
    margin: 0 10px;
  }

  a {
    color: #42b983;
  }

  .dog-component {
    margin-left: 10px;
  }
</style>



// WEBPACK FOOTER //
// src/components/DogComponent.vue